const {CUSTOMER_TYPE, CUSTOMER_STATUS} = require("./CustomerConstant");
const moment = require("moment");
const {fsMomentImport} = require("../utils/utils");
const Address = require("../utils/Address")
const File = require("../file/File");
const {EMAIL_RE} = require("../user/UserConstant");
const PaymentMethod = require("../payment/PaymentMethod");

class Customer {

    constructor({
                    id = '',
                    additionalID = {},
                    type = CUSTOMER_TYPE.PERSON,
                    status = CUSTOMER_STATUS.NEW,
                    address = new Address(),
                    firstName = '',
                    lastName = '',
                    phone = '',
                    email = '',
                    birthday = moment(),
                    name = '',
                    foundationDate = moment(),
                    commercialRegister = '',
                    commercialRegisterNumber = '',

                    created = moment(),
                    createdBy = '',
                    updated = moment(),
                    updatedBy = '',

                    file = [],
                    paymentMethod = [],
                } = {}) {

        this.id = id;
        this.additionalID = additionalID;
        this.type = type;
        this.status = status;
        this.address = new Address(address)
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
        this.email = email;
        this.name = name;
        this.commercialRegister = commercialRegister;
        this.commercialRegisterNumber = commercialRegisterNumber;

        this.birthday = null;
        this.foundationDate = null;
        this.created = null;
        this.updated = null;
        const field = ['birthday', 'foundationDate', 'created', 'updated'];
        [birthday, foundationDate, created, updated].forEach((date, index) => {
            date = fsMomentImport(date)
            this[field[index]] = date && date.isValid() ? date : null
        })
        this.createdBy = createdBy;
        this.updatedBy = updatedBy;

        // preset address
        if (!this.address.fullName && this.fullName) {
            this.address.fullName = this.fullName
        }
        if (this.type === CUSTOMER_TYPE.COMPANY && !this.address.addressLine1 && this.name) {
            this.address.addressLine1 = this.name
        }

        this.file = Array.isArray(file) ? file : [];
        this.paymentMethod = Array.isArray(paymentMethod) ? paymentMethod : [];
    }

    static get FS_COLLECTION_NAME() {
        return 'customer'
    }

    static get STORAGE_DIR_NAME() {
        return 'customer'
    }

    static get CUSTOMER_TYPE() {
        return CUSTOMER_TYPE
    }

    static get CUSTOMER_STATUS() {
        return CUSTOMER_STATUS
    }

    get fullName() {
        return `${this.firstName} ${this.lastName}`
    }

    get displayName(){
        return this.type === CUSTOMER_TYPE.PERSON ? this.fullName : this.name;
    }

    get storageHomeDirectory() {
        return `${Customer.STORAGE_DIR_NAME}/${this.id}`
    }

    get requiredFileCategory() {
        if (this.type === CUSTOMER_TYPE.PERSON) {
            return File.REQUIRED_PERSON_FILE_CATEGORY;
        }
        return File.REQUIRED_COMPANY_FILE_CATEGORY;
    }

    /**
     * @function missingFileCategory
     * it loops through all files and check if all requiredFileCategory are present
     * @return array of missing file types
     */
    get missingFileCategory() {
        return this.requiredFileCategory
            .filter((fileCategory) => this.file.every((f) => f.category !== fileCategory))
    }

    get fileMissing() {
        return this.missingFileCategory.length !== 0
    }

    validate(attribute) {

        if (attribute === undefined) {
            const attributes = Object.keys(this);
            try {
                attributes.forEach(attribute => {
                    if (!this._validate(attribute)) {
                        console.log(attribute)
                        throw "validation error"
                    }
                });
            } catch (e) {
                return false
            }
            return true
        }

        return this._validate(attribute)
    }

    _validate(attribute) {

        try {
            switch (attribute) {
                case 'id':
                    return (typeof this[attribute] === 'string');
                case 'additionalID':
                    return true;
                case 'type':
                    return Object.values(CUSTOMER_TYPE).includes(this[attribute]);
                case 'status':
                    return Object.values(CUSTOMER_STATUS).includes(this[attribute]);
                case 'address':
                    return this.address.validate();
                case 'firstName':
                case 'lastName':
                    if (this.type === CUSTOMER_TYPE.COMPANY) {
                        return true;
                    }
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length !== 0)
                        && (this[attribute].length <= 50);
                case 'name':
                    if (this.type === CUSTOMER_TYPE.PERSON) {
                        return true;
                    }
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length !== 0)
                        && (this[attribute].length <= 50);
                case 'phone':
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length !== 0)
                        && (this[attribute].length <= 20);
                case 'email':
                    return EMAIL_RE.test(String(this[attribute]).toLowerCase())
                        && (this[attribute].length <= 255);
                case 'commercialRegister':
                case 'commercialRegisterNumber':
                    return true;
                case 'birthday':
                case 'foundationDate':
                case 'created':
                case 'updated':
                    return this[attribute] === null || this[attribute] instanceof moment
                case 'createdBy':
                case 'updatedBy':
                    return (typeof this[attribute] === 'string')
                case 'file':
                case 'paymentMethod':
                    return Array.isArray(this[attribute])
                default:
                    return false
            }
        } catch (e) {
            console.error('Validating Customer with error:', e, attribute);
            return false
        }
    }

    toFirestore() {
        const file = {}, paymentMethod = {};
        try {
            this.file.forEach((f) => file[f.id] = f.toFirestore());
            this.paymentMethod.forEach((p) => paymentMethod[p.id] = p.toFirestore());
        } catch (e) {
            console.log('Could not convert files or paymentMethod')
        }

        let data = {
            id: this.id,
            additionalID: this.additionalID,
            type: this.type,
            status: this.status,
            phone: this.phone,
            address: this.address.toFirestore ? this.address.toFirestore() : {},
            created: this.created && (typeof this.created.toDate === 'function') ? this.created.toDate() : null,
            createdBy: this.createdBy,
            updated: this.updated && (typeof this.updated.toDate === 'function') ? this.updated.toDate() : null,
            updatedBy: this.updatedBy,
            email: this.email,
            file,
            paymentMethod,
        }

        switch (this.type) {
            case CUSTOMER_TYPE.COMPANY:
                data = {
                    ...data,
                    name: this.name,
                    foundationDate: this.foundationDate && (typeof this.foundationDate.toDate === 'function') ?
                        this.foundationDate.toDate() : null,
                    commercialRegister: this.commercialRegister,
                    commercialRegisterNumber: this.commercialRegisterNumber,
                }
                break;
            case CUSTOMER_TYPE.PERSON:
                data = {
                    ...data,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    birthday: this.birthday && (typeof this.birthday.toDate === 'function') ?
                        this.birthday.toDate() : null,
                }
                break;
            default:
        }

        return data
    }

    static parseFirestore(customer) {
        return new Customer({
            ...customer,
            address: Address.parseFirestore(customer.address),
            file: Object.values(File.fromFirestore(customer.file)),
            paymentMethod: Object.values(PaymentMethod.fromFirestore(customer.paymentMethod))
        })
    }

    static fromFirestore(fsSnap) {

        let data = {};
        if (fsSnap) {
            Object.keys(fsSnap).forEach((key) => {
                // the firestore object is not null
                if (fsSnap[key]) {
                    data[key] = Customer.parseFirestore(fsSnap[key], key)
                }
            });
        }

        return data
    }

    isSame(object) {

        return Object.is(
            JSON.stringify(object),
            JSON.stringify(this))
    }
}

module.exports = Customer;